
import { Helmet } from "react-helmet";
import { seoConfig } from "./config";

interface MetaTagsProps {
  title?: string;
  description?: string;
  canonicalUrl: string;
  imageUrl?: string;
}

export const MetaTags = ({
  title = seoConfig.defaultTitle,
  description = seoConfig.defaultDescription,
  canonicalUrl,
  imageUrl = seoConfig.defaultImage
}: MetaTagsProps) => {
  const absoluteImageUrl = imageUrl.startsWith('http') ? imageUrl : `${seoConfig.baseUrl}${imageUrl}`;
  const absoluteCanonicalUrl = canonicalUrl.startsWith('http') ? canonicalUrl : `${seoConfig.baseUrl}${canonicalUrl}`;

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content="Aisne, Laon, Soissons, Saint-Quentin, réservation locale, gîte Aisne, restaurant Aisne, hébergement Aisne, tourisme local, plateforme réservation, événements Aisne, activités Aisne, sortir dans l'Aisne" />
      
      {/* Essential OpenGraph tags for WhatsApp and other messaging apps */}
      <meta property="og:site_name" content="NLocale" />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={absoluteImageUrl} />
      <meta property="og:image:secure_url" content={absoluteImageUrl} />
      <meta property="og:image:type" content="image/png" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="NLocale - Découvrez l'Aisne autrement" />
      <meta property="og:url" content={absoluteCanonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content="fr_FR" />
      
      {/* WhatsApp specific */}
      <meta property="og:app_id" content="310633997" />
      <meta property="og:image:secure_url" content={absoluteImageUrl} />
      <meta property="og:video" content="" />
      <meta property="al:ios:url" content={absoluteCanonicalUrl} />
      <meta property="al:ios:app_store_id" content="310633997" />
      <meta property="al:ios:app_name" content="WhatsApp" />
      <meta property="al:android:url" content={absoluteCanonicalUrl} />
      <meta property="al:android:package" content="com.whatsapp" />
      <meta property="al:android:app_name" content="WhatsApp" />
      
      {/* Telegram */}
      <meta name="telegram:channel" content="@nlocalefr" />
      <meta property="telegram:image" content={absoluteImageUrl} />
      
      {/* Facebook Messenger */}
      <meta property="fb:app_id" content="61572375376059" />
      <meta property="messenger:image" content={absoluteImageUrl} />
      
      {/* Twitter Card - also used by some messaging apps */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@NLocaleFr" />
      <meta name="twitter:creator" content="@NLocaleFr" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={absoluteImageUrl} />
      <meta name="twitter:image:alt" content="NLocale - Découvrez l'Aisne autrement" />
      <meta name="twitter:url" content={absoluteCanonicalUrl} />
      
      {/* Additional SEO tags */}
      <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="revisit-after" content="7 days" />
      <meta name="author" content="NLocale" />
      <meta name="geo.region" content="FR-02" />
      <meta name="geo.placename" content="Aisne, Laon, Soissons, Saint-Quentin" />
      
      {/* Canonical and alternate */}
      <link rel="canonical" href={absoluteCanonicalUrl} />
      <link rel="alternate" hrefLang="fr-fr" href={absoluteCanonicalUrl} />
      
      {/* Mobile app meta */}
      <meta name="application-name" content="NLocale" />
      <meta name="mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="NLocale" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Theme and icons */}
      <meta name="theme-color" content="#F97316" />
      <meta name="msapplication-TileColor" content="#F97316" />
      
      {/* Performance */}
      <link rel="preconnect" href={seoConfig.baseUrl} />
      <link rel="dns-prefetch" href={seoConfig.baseUrl} />

      {/* Schema.org JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "NLocale",
          "description": description,
          "image": absoluteImageUrl,
          "logo": absoluteImageUrl,
          "url": absoluteCanonicalUrl,
          "sameAs": [
            seoConfig.social.facebook,
            seoConfig.social.twitter,
            seoConfig.social.instagram,
            seoConfig.social.telegram,
            seoConfig.social.whatsapp,
            seoConfig.social.messenger
          ],
          "address": {
            "@type": "PostalAddress",
            "addressRegion": "Hauts-de-France",
            "addressCountry": "FR"
          }
        })}
      </script>
    </Helmet>
  );
};
